import type {
  MbcBriefAssetChange,
  MbcBriefAssetFile,
  MbcBriefAssetLink,
} from "@nlc/shared-types";

/**
 * Uploaded file reference — the file lives in S3, metadata in the MySQL
 * `attachments` row (`attachmentId`). Mirrors `assetFileSchema` in the AI
 * module, which rejects an entry missing `attachmentId` or `url`.
 */
export type AssetFileRef = MbcBriefAssetFile;

/** A shared link (no upload needed). */
export type AssetLinkRef = MbcBriefAssetLink;

/** A brief file-and-link group — `assets` or `inspirations`. */
export type AssetGroup = {
  files: AssetFileRef[];
  links: AssetLinkRef[];
};

/** In-flight upload shown in the block until it resolves to an `AssetFileRef`. */
export type PendingUpload = {
  file: File;
  id: string;
  status: "error" | "uploading";
};

export const emptyGroup = (): AssetGroup => ({ files: [], links: [] });

export const groupCount = (group: AssetGroup): number =>
  group.files.length + group.links.length;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Reads an asset group out of the brief working memory.
 *
 * Entries the AI module's `assetFileSchema`/`assetLinkSchema` would reject are
 * discarded instead — they can never render or reach submit, and keeping one
 * would 400 every subsequent save rather than just itself.
 */
export function readGroup(raw: unknown): AssetGroup {
  const source = (raw ?? {}) as {
    files?: unknown;
    links?: unknown;
  };

  const files = (Array.isArray(source.files) ? source.files : [])
    .filter((entry): entry is Record<string, unknown> => isRecord(entry))
    .filter(
      (file) =>
        typeof file.attachmentId === "number" && typeof file.url === "string",
    )
    .map((file) => ({
      ...file,
      name: typeof file.name === "string" ? file.name : "file",
    })) as AssetGroup["files"];

  const links = (Array.isArray(source.links) ? source.links : [])
    .filter((entry): entry is Record<string, unknown> => isRecord(entry))
    .filter((link) => typeof link.url === "string")
    .map((link) => ({ url: link.url as string }));

  return { files, links };
}

/** Additions to / removals from one group; the target is added when sent. */
export type AssetGroupChange = Omit<MbcBriefAssetChange, "target">;

/** Applies a change to a local group the way the AI service applies it to the stored one. */
export function applyGroupChange(
  group: AssetGroup,
  change: AssetGroupChange,
): AssetGroup {
  const removed = new Set(change.removeLinks);
  const links = group.links.filter((link) => !removed.has(link.url));

  for (const url of change.addLinks ?? []) {
    if (!links.some((link) => link.url === url)) links.push({ url });
  }

  return { files: group.files, links };
}
